import { sort } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { StaticCustomType as StaticCustomTypeInternal } from "@prismicio/types-internal/lib/customtypes"
import {
  QueryClient,
  queryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

const CustomType = z.object({
  id: z.string(),
  label: z.string(),
  repeatable: z.boolean(),
  status: z.boolean(),
})

export type CustomType = z.TypeOf<typeof CustomType>

async function getCustomTypes(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
) {
  const customTypes = await request(new URL("customtypes", baseUrl), {
    headers: {
      repository: repository,
    },
    credentials: getRequestCredentials(authStrategy),
    okType: z.array(CustomType),
  })

  return sort(customTypes, (customType) => customType.label)
}

interface CustomTypesOptionsArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
}

export function getCustomTypesOptions(args: CustomTypesOptionsArgs) {
  const { baseUrl, repository, authStrategy } = args

  return queryOptions({
    queryKey: ["customTypes"],
    queryFn: () => getCustomTypes(baseUrl, repository, authStrategy),
  })
}

export function prefetchCustomTypes(
  queryClient: QueryClient,
  args: CustomTypesOptionsArgs,
) {
  void queryClient.prefetchQuery(getCustomTypesOptions(args))
}

export function fetchCustomTypes(
  queryClient: QueryClient,
  args: CustomTypesOptionsArgs,
) {
  return queryClient.fetchQuery(getCustomTypesOptions(args))
}

export function useCustomTypes(args: CustomTypesOptionsArgs) {
  return useSuspenseQuery(getCustomTypesOptions(args))
}

interface CustomTypeOptionsArgs extends CustomTypesOptionsArgs {
  id: string
}

// Note: the response size for the full list of custom types (without static=true)
// is pretty small so we just reuse it to improve our cache hit ratio.
export function useCustomType(args: CustomTypeOptionsArgs) {
  const { id, baseUrl, repository, authStrategy } = args

  const customTypes = useCustomTypes({
    baseUrl,
    repository,
    authStrategy,
  }).data

  const customType = customTypes.find((customType) => customType.id === id)
  if (!customType) throw new Error(`Custom type "${id}" does not exist`)

  return customType
}

export function invalidateCustomTypes(
  queryClient: QueryClient,
  args: CustomTypesOptionsArgs,
) {
  return queryClient.invalidateQueries(getCustomTypesOptions(args))
}

//--------------------------------------
//  Static custom type
//--------------------------------------

// Note: A static custom type has a lot more data than a regular custom type:
// All the shared slice references are replaced with the slice data.
// A static custom type response is a convenient way to pre-package custom type + slice data
// but it's not scalable across multiple custom types as all the shared slices get repeated many times.

function getStaticCustomType(args: CustomTypeOptionsArgs) {
  const { id, baseUrl, repository, authStrategy } = args

  const url = new URL(`customtypes/${id}`, baseUrl)
  url.searchParams.set("static", "true")

  return request(url, {
    headers: { repository },
    credentials: getRequestCredentials(authStrategy),
    okType: StaticCustomType,
  })
}

const StaticCustomType = z.unknown().transform((val, ctx) => {
  const result = StaticCustomTypeInternal.decode(val)
  if (result._tag === "Right") return result.right

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Invalid custom type.",
  })

  return z.NEVER
})

function getStaticCustomTypeOptions(args: CustomTypeOptionsArgs) {
  const { id } = args

  return queryOptions({
    queryKey: ["customTypes", "static", id],
    queryFn: () => getStaticCustomType(args),
  })
}

export function useStaticCustomType(args: CustomTypeOptionsArgs) {
  return useSuspenseQuery(getStaticCustomTypeOptions(args))
}

export function prefetchStaticCustomType(
  queryClient: QueryClient,
  args: CustomTypeOptionsArgs,
) {
  void queryClient.prefetchQuery(getStaticCustomTypeOptions(args))
}
